import { Component, OnInit } from '@angular/core';
import { SelectorItem } from '@app/component/selector/selector.component';
import {
  PaymentMethod,
  PaymentMethodVM,
} from '@app/model/payment-request/payment-request.dto';
import { ActivatedRoute, Router } from '@angular/router';
import { LoggingService } from '@app/service/logging-service/logging.service';
import { QueryParamsService } from '@app/service/query-params/query-params.service';
import {
  AvailablePaymentMethods,
  CheckoutStatus,
  StateService,
} from '@app/service/state/state.service';
import { ValuePipe } from '@app/shared/pipe/value.pipe';
import { RouteComponent } from '@app/route/route.component';
import { HeaderService } from '@app/service/header/header.service';
import { routeNames } from '@assets/val/route-constants';

interface Methodtranslations {
  title: string;
  subTitle: string;
  bulletPoints: string[];
}

@Component({
  selector: 'pia-payment-method-selection',
  templateUrl: './pia-payment-method-selection.component.html',
  styleUrls: [
    './pia-payment-method-selection.component.scss',
    '../../page-shared.css',
  ],
})
export class PiaPaymentMethodSelectionComponent
  extends RouteComponent
  implements OnInit
{
  loading = true;
  paymentMethodsVM: PaymentMethodVM[] = null;
  selectedPaymentMethodVM: PaymentMethodVM = null;
  navigationInProgress: boolean = false;
  selectorItems: SelectorItem[] = null;
  selectedItem: SelectorItem = null;
  modalActive: boolean = false;
  noAvailablePaymentMethods = false;
  color = '#DB305E';
  invalidPRStateForProceeding = false;

  constructor(
    private queryParamsService: QueryParamsService,
    private loggingService: LoggingService,
    private valuePipe: ValuePipe,
    protected router: Router,
    protected stateService: StateService,
    protected headerService: HeaderService,
    protected route: ActivatedRoute
  ) {
    super(router, route, stateService, headerService);
  }

  async ngOnInit(): Promise<void> {
    if (
      this.state?.status === CheckoutStatus?.CANCELLED ||
      this.state?.status === CheckoutStatus?.EXPIRED
    ) {
      this.invalidPRStateForProceeding = true;
      return;
    }
    const token = this.queryParamsService.getQueryParams().id;
    try {
      const paymentMethods: AvailablePaymentMethods[] =
        await this.stateService.getAvailablePaymentMethods(token);

      if (!paymentMethods || paymentMethods?.length === 0) {
        this.noAvailablePaymentMethods = true;
      }

      // Filters away other payment methods
      const piaPaymentMethods = paymentMethods.filter(
        (method) => method.type === PaymentMethod.PAY_IN_ADVANCE
      );

      if (!piaPaymentMethods || piaPaymentMethods?.length === 0) {
        this.loggingService.warn(
          'Payment request attempted to load PIA flow despite not having PIA as a payment method, methods: ' +
            JSON.stringify(paymentMethods)
        );

        // Navigates to frontend driven flow if PIA is not a payment method for current token.
        this.router.navigate([`/${routeNames.HOME}`], {
          queryParamsHandling: 'preserve',
        });
        return;
      }

      // Uses only the PIA method if one is found.
      this.paymentMethodsVM = this.transformDtoToVm(piaPaymentMethods);

      const getPaymentMethodByType = this.paymentMethodsVM?.find(
        (method) =>
          method?.type === this.state.paymentInformation?.paymentMethod
      );

      this.selectedPaymentMethodVM =
        getPaymentMethodByType || this.paymentMethodsVM[0];

      this.loading = false;
    } catch (err) {
      this.handleError(err);
    }
  }

  async handleContinueClick(): Promise<void> {
    if (this.navigationInProgress) {
      return;
    }

    this.loggingService.warn(
      'selected payment method and navigating: ' +
        this.selectedPaymentMethodVM.type
    );

    this.navigationInProgress = true;
    try {
      await this.stateService
        .registerChosenMethod(this.selectedPaymentMethodVM.id)
        .then((checkoutState) => {
          const route = this.stateService.getRouteFromState(checkoutState);
          this.router.navigate([route], { queryParamsHandling: 'preserve' });
        });
    } catch (error) {
      console.log(error);
    }
    this.navigationInProgress = false;
  }

  deactivateModal(): void {
    this.modalActive = false;
  }

  getModalPurchaseInfo(): void {
    this.selectorItems = [
      {
        tab: 'Purchase',
        title: 'Summary',
        descriptions: [{ text: this.state.paymentInformation.description }],
        lineItems: this.state.paymentInformation?.lineItems,
        totalValue: `Total incl. VAT: ${this.valuePipe.transform(
          this.state.totalCheckoutValue
        )}`,
      },
      {
        tab: 'Terms',
        title: 'Pay in advance',
        descriptions: [
          {
            text: `With Zaver’s Pay in Advance your order will be processed as soon as we receive payment from you. Use the payment information displayed on the order confirmation page here in the checkout, or find the email and pdf invoice sent to the email address you specified.`,
          },
        ],
      },
    ];

    this.selectedItem = this.selectorItems[0];
    this.modalActive = true;
  }

  openModalWithTerms(): void {
    this.getModalPurchaseInfo();
  }

  selectPaymentMethod(method: PaymentMethodVM): void {
    this.selectedPaymentMethodVM = method;
  }

  transformDtoToVm(
    paymentMethods: AvailablePaymentMethods[]
  ): PaymentMethodVM[] {
    return paymentMethods.map((method) => {
      switch (method.type) {
        case PaymentMethod.PAY_IN_ADVANCE:
          return {
            id: method.id,
            type: method.type,
            title: this.getMethodtranslations(method).title,
            subTitle: this.getMethodtranslations(method).subTitle,
            zaverIcon: { name: 'invoice', size: 26 },
            bulletPoints: this.getMethodtranslations(method).bulletPoints,
          };
        default:
          return null;
      }
    });
  }

  getMethodtranslations(method: AvailablePaymentMethods): Methodtranslations {
    switch (method.type) {
      case PaymentMethod.PAY_IN_ADVANCE:
        return {
          title: 'Pay in advance',
          subTitle: 'Regular invoice',
          bulletPoints: ['Pay in EUR from any bank across Europe'],
        };
    }
  }
}
